import axios from 'axios';
import { camelCase, find, inRange, isEmpty, startCase } from 'lodash';
import queryString from 'query-string';
import {
  CHECK_TASK_STATAUS,
  CHECK_TASK_STATUS,
  DOMAIN_NAME,
  GENERIC_APP_CONNECT,
  GENERIC_IMAGE_DOWNLOAD,
  PIXABAY_VIDEO_COVER_ENDPOINT
} from '../_actions/endpoints';
import {
  ENTER_ANIMATION,
  EXIT_ANIMATION,
  IconsSources,
  ImageAppSources,
  ImageSources,
  MusicSources,
  SourceTypes,
  VideoSources
} from '../_actions/types';
import {
  FABRIC_CIRCLE_ELEMENT,
  FABRIC_ELLIPSE_ELEMENT,
  FABRIC_GIF_ELEMENT,
  FABRIC_GROUP_ELEMENT,
  FABRIC_IMAGE_ELEMENT,
  FABRIC_ITEXT_ELEMENT,
  FABRIC_POLYGON_ELEMENT,
  FABRIC_RECT_ELEMENT,
  FABRIC_SVG_ELEMENT,
  FABRIC_TEXT_ELEMENT,
  FABRIC_TEXTBOX_ELEMENT,
  FABRIC_TRIANGLE_ELEMENT,
  FABRIC_VIDEO_ELEMENT,
  FeatureCodes
} from '../_components/details/constants';
import {
  ANALYTICS,
  DOCUMENTS,
  FAVORITES,
  LAYOUTS,
  MAGICAL,
  MY_APPS,
  PROJECTS,
  PUBLISHING,
  QUICK_START,
  ROOT,
  TEMPLATES_SCREEN
} from './routes';
import Analytics from 'analytics';
import googleTagManager from '@analytics/google-tag-manager';
import Format from 'string-format';
import { defaultMusicPayload } from '../_components/canvas/constants/defaults';
import { ImageMimeTypes, ROLE_WORKSPACE_ADMIN_CODE, ROLE_WORKSPACE_OWNER_CODE } from './constants';
import { STILL_EFFECT } from '../_components/details/advanced/animations/defaultLayerAnimations';
import store from '../store';

import { fabric } from 'fabric';
import { showToast } from './showToast';

const format = require("string-format");

export function pointToPixel(point) {
  // 1px = 0.75 point
  return point / 0.75;
}

export function pixelToPoint(pixel) {
  // 1px = 0.75 point
  return pixel * 0.75;
}

export function vhToPixel(value) {
  return (value * document.documentElement.clientHeight) / 100;
}

export const fontNormalBase = 0.04;

export const getMinSideBase = (contentSize) => {
  return Math.min(contentSize.width, contentSize.height);
};

export const getNormalFontSize = (contentSize) => {
  return fontNormalBase * getMinSideBase(contentSize);
};

export const convertCanvasObjectToDataURL = (canvasRef) => {
  return new Promise(function (resolve, reject) {
    let dataUrl = canvasRef.handler.getObjectImageDataUrl();
    if (dataUrl) {
      resolve(dataUrl);
    } else {
      reject("failed to convert object to image");
    }
  });
};

export const downloadFile = (dataUrl, fileName) => {
  var link = document.createElement("a");
  link.download = fileName;
  link.href = dataUrl;
  link.click();
};

export const convertPixelToFloat = (pixelString) => {
  if (!pixelString) {
    return 0;
  }
  if (typeof pixelString === "number") {
    return pixelString;
  }
  return parseFloat(pixelString.slice(0, -2));
};

export const convertNumberToPixel = (string) => {
  return string + "px";
};

export const getAlignPosition = (
  elementSize,
  elementCurrentPosition,
  alignType,
  contentSize
) => {
  let x = elementCurrentPosition.x;
  let y = elementCurrentPosition.y;

  elementSize = {
    height: convertPixelToFloat(elementSize.height),
    width: convertPixelToFloat(elementSize.width),
  };

  switch (alignType) {
    case "top":
      y = 0;
      break;
    case "middle":
      y = (contentSize.height - elementSize.height) / 2;
      break;
    case "bottom":
      y = contentSize.height - elementSize.height;
      break;
    case "left":
      x = 0;
      break;
    case "middle-center":
      x = (contentSize.width - elementSize.width) / 2;
      y = (contentSize.height - elementSize.height) / 2;
      break;
    case "center":
      x = (contentSize.width - elementSize.width) / 2;
      break;
    case "right":
      x = contentSize.width - elementSize.width;
      break;
    default:
      x = elementCurrentPosition.x;
      y = elementCurrentPosition.y;
  }

  return { x: x, y: y };
};

/*
It helps to convert JPEG/PNG dataURI into blob
*/
export const dataURItoBlob = (dataURI) => {
  // convert base64/URLEncoded data component to raw binary data held in a string
  var byteString;
  if (dataURI.split(",")[0].indexOf("base64") >= 0)
    byteString = atob(dataURI.split(",")[1]);
  else byteString = unescape(dataURI.split(",")[1]);

  // separate out the mime component
  var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

  // write the bytes of the string to a typed array
  var ia = new Uint8Array(byteString.length);
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type: mimeString });
};

export const convertImageToBase64 = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = (event) => {
        reject(event.error || new Error("Could not read image"));
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.onerror = (event) => {
      reject(event.error || new Error("Could not load image"));
    };
    xhr.open("GET", imageUrl);
    xhr.responseType = "blob";
    xhr.send();
  });
};

const defaultImageWidth = 600; // 200px
export const getImagePayload = (url, size) => {
  const imageAspectRatio = size.width / size.height;
  const imageHeight = defaultImageWidth / imageAspectRatio;

  return {
    mime: "image",
    payload: {
      name: "New Image",
      src: url,
      type: "image",
      height: imageHeight,
      width: defaultImageWidth,
    },
    content: {
      url: url,
    },
  };
};

export const getHeightAndWidth = (file) => {
  return new Promise(function (resolve, reject) {
    if (
      !ImageMimeTypes.includes(file.type) ||
      // Note: heic and heif extensions are not readable from web so add this patch to put dummy height and width
      ["image/heic", "image/heif"].includes(file.type)
    ) {
      resolve({ width: 300, height: 300 });
    }

    const image = new Image();
    image.onload = () => {
      resolve({ width: image.width, height: image.height });
    };

    image.onerror = (error) => {
      reject(error);
    };
    image.src = URL.createObjectURL(file);
  });
};

export const setAxiosAuthHeaders = (token, workspaceId) => {
  if (token && workspaceId) {
    axios.defaults.headers.common = {
      Authorization: `Token ${token}`,
      Organization: workspaceId,
    };
  } else if (token && workspaceId == null) {
    axios.defaults.headers.common = {
      Authorization: `Token ${token}`,
    };
    delete axios.defaults.headers.common["Organization"];
  } else {
    // Delete auth header
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["Organization"];
  }
};

export const setAxiosWorkspaceHeader = (workspaceId) => {
  if (workspaceId) {
    axios.defaults.headers.common = {
      ...axios.defaults.headers.common,
      Organization: workspaceId,
    };
  } else {
    delete axios.defaults.headers.common["Organization"];
  }
};

export const getAuthToken = () => localStorage.getItem("Token");

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("Token", token);
  } else {
    localStorage.removeItem("Token");
  }
};

export const getRootElementIdOfGroup = (layers, layerId) => {
  if (layers[layerId].parent === null) {
    return layerId;
  } else {
    return getRootElementIdOfGroup(layers, layers[layerId].parent);
  }
};

export const modifyQuillCursorStyling = (cursorId) => {
  let cursorSelectionBlocks = document.querySelectorAll(
    `#ql-cursor-${cursorId} .ql-cursor-selection-block`
  );
  if (cursorSelectionBlocks) {
    Array.from(cursorSelectionBlocks).forEach((block) => {
      block.style.setProperty(
        "transform-origin",
        `-${block.style.left} -${block.style.top} 0`
      );
    });
    let cursorCaretContainer = document.querySelector(
      `#ql-cursor-${cursorId} .ql-cursor-caret-container`
    );
    cursorCaretContainer.style.setProperty(
      "transform-origin",
      `-${cursorCaretContainer.style.left} -${cursorCaretContainer.style.top} 0`
    );

    let cursorFlag = document.querySelector(
      `#ql-cursor-${cursorId} .ql-cursor-flag`
    );
    cursorFlag.style.setProperty(
      "transform-origin",
      `-${cursorFlag.style.left} -${cursorFlag.style.top} 0`
    );
    cursorFlag.style.setProperty(
      "top",
      `${convertNumberToPixel(convertPixelToFloat(cursorFlag.style.top) + 20)}`
    );
  }
};

export const addBroadCastedCursor = (
  userCursorInfo,
  broadCastedCursor,
  layerId
) => {
  for (let userPk in userCursorInfo) {
    if (userCursorInfo.hasOwnProperty(userPk)) {
      let cursorColor = "#" + Math.floor(Math.random() * 16777215).toString(16);
      let cursorId = `${userPk}_${layerId}`;
      broadCastedCursor.createCursor(
        cursorId,
        userCursorInfo[userPk].user?.full_name
          ? userCursorInfo[userPk].user?.full_name
          : userCursorInfo[userPk].user?.username,
        cursorColor
      );

      if (userCursorInfo[userPk].cursor !== null) {
        broadCastedCursor.moveCursor(cursorId, userCursorInfo[userPk].cursor);
        broadCastedCursor.toggleFlag(cursorId, true);
        modifyQuillCursorStyling(cursorId);
      } else {
        broadCastedCursor.toggleFlag(cursorId, false);
        broadCastedCursor.removeCursor(cursorId);
      }
    }
  }
};

const svgAsURLPayload = (url) => {
  return {
    loadType: null,
    svg: url,
  };
};

export const prepareAssetPayload = (asset, activePageId) => {
  asset.thumbnail_width = asset.thumbnail_width || 200;
  asset.thumbnail_height = asset.thumbnail_height || 200;
  const message = {
    mime: asset.asset_type,
    type: asset.asset_type === "shape" ? "icon" : asset.asset_type,
    payload: {
      name: asset.asset_key || "New Asset",
      src: asset.thumbnail,
      type: getFabricTypeFromMime(asset.asset_type),
      height: asset.thumbnail_height,
      width: asset.thumbnail_width,
      cover: asset.thumbnail_cover_image,
      userProperty: {
        source_id: asset.id,
      },
    },
    content: {
      meta: asset,
      url: asset.thumbnail,
      source: SourceTypes.TLDR_ASSETS,
      source_id: asset.id,
      source_url: asset.thumbnail,
    },
  };

  if (asset.asset_type === "video") {
    message.payload.autoplay = false;
    message.payload.loop = false;
    message.payload.muted = false;
    message.payload.container = activePageId;
    // message.payload.cover = asset.thumbnail; // Use image instead of video in the cover
    message.payload.startTime = 0;
    message.payload.endTime = asset.payload?.duration;
    message.payload.duration = asset.payload?.duration;
    message.content.meta.duration = asset.payload?.duration;
  } else if (asset.asset_type === "audio") {
    return getMusicPayload(MusicSources.ASSETS, {
      ...message,
      cover: asset?.thumbnail_cover_image,
      title: asset.payload?.title,
    });
  } else if (asset.asset_type === "shape") {
    return prepareIconPayload(asset, SourceTypes.TLDR_ASSETS);
  }
  return message;
};

export const prepareGifPayload = (source, giphy) => {
  const { url, height, width, title } = giphy.images.original;

  return {
    mime: "giphy",
    payload: {
      name: title,
      src: url,
      type: "gif",
      height: Number(height),
      width: Number(width),
      cover: giphy.images?.preview_gif?.url,
      userProperty: {
        source_id: giphy?.id,
      },
    },
    content: {
      source: SourceTypes.GIPHY,
      source_id: giphy?.id,
      source_url: url,
      url: url,
      meta: giphy,
    },
  };
};

export const prepareIconPayload = (data, source = null) => {
  const tldrPayload = {
    mime: "shape",
    type: "icon",
    payload: {
      type: FABRIC_SVG_ELEMENT,
      superType: "svg",
      userProperty: {
        // source_id: data.id,
        source_id: null,
      },
    },
    content: {
      source: SourceTypes.FLATICON,
      source_url: null,
      source_id: null,
      meta: {},
    },
  };

  switch (source) {
    case IconsSources.FLATICON:
      let imageUrl = data?.images?.svg;
      const coverUrl =
        data?.images?.png["512"] || data?.images?.png["128"] || null;

      if (!imageUrl) {
        tldrPayload.mime = "image";
        tldrPayload.payload = {
          name: "New Image",
          src: coverUrl,
          type: "image",
          height: 250,
          width: 250,
          cover: coverUrl,
          userProperty: {
            source_id: data.id,
          },
        };
      }
      tldrPayload.payload.cover = coverUrl;
      tldrPayload.content.source = SourceTypes.FLATICON;
      tldrPayload.content.source_id = data.id;
      tldrPayload.content.source_url = imageUrl ?? coverUrl;
      tldrPayload.content.preview_url = imageUrl;
      tldrPayload.content.meta = {
        tags: data?.tags,
        images: data?.images,
      };
      break;
    case IconsSources.NOUN_PROJECT:
      tldrPayload.payload.userProperty.source_id = data.id;
      tldrPayload.payload.cover = data?.preview_url;
      tldrPayload.content.source = SourceTypes.NOUN_PROJECT;
      tldrPayload.content.source_id = data.id;
      tldrPayload.content.source_url = data?.icon_url;
      tldrPayload.content.preview_url = data?.preview_url;
      tldrPayload.content.meta = {
        tags: data?.tags,
        images: data?.images,
      };
      break;
    case SourceTypes.TLDR_ASSETS:
      tldrPayload.payload.userProperty.source_id = data.id;
      tldrPayload.payload.cover = data?.thumbnail_cover_image;
      tldrPayload.content.source = SourceTypes.TLDR_ASSETS;
      tldrPayload.content.source_id = data.id;
      tldrPayload.content.source_url = data?.thumbnail;
      tldrPayload.content.preview_url = data?.thumbnail_cover_image;
      tldrPayload.content.meta = {
        id: data?.id,
        tags: data?.tags,
        images: data?.images,
      };
      break;
    default:
  }

  const url = tldrPayload.content.source_url;
  if (!isURL(url)) {
    console.warn(`Invalid URL: ${url}`);
    return;
  }

  // const payload = await svgAsStringPayload(url);

  // To load svgAsURL
  const payload = svgAsURLPayload(url);

  return {
    ...tldrPayload,
    payload: {
      ...tldrPayload.payload,
      ...payload,
    },
  };
};

export const getUserMetaData = (source, data) => {
  let user = { source: source };
  if (source === ImageSources.UNSPLASH) {
    user["source_location"] = "https://unsplash.com/";
    user["username"] = data?.user?.username;
    user["profile"] = data?.user?.links?.html;
    user["urls"] = data?.urls;
    user["links"] = data?.links;
  } else if (source === ImageSources.PEXELS) {
    user["source_location"] = "https://pexels.com/";
    user["username"] = data?.photographer;
    user["profile"] = data?.photographer_url;
  } else if (source === ImageSources.PIXABAY) {
    user["source_location"] = "https://pixabay.com/";
    user["user"] = data?.user;
    user["urls"] = data?.userImageURL;
  } else if (source === ImageSources.SHOPIFY) {
    user["source_location"] = "";
    user["user"] = data?.node?.title;
    user["urls"] = "";
  } else if (source === ImageSources.SHUTTERSTOCK) {
    user["source_location"] = "";
    user["user"] = data?.contributor?.id;
    user["urls"] = "";
  } else if (source === ImageSources.STORYBLOCKS) {
    user["source_location"] = "";
    user["user"] = data?.title;
    user["urls"] = "";
  }
  return user;
};

export const getMIMETypeFromFabricObject = (objType) => {
  switch (objType) {
    case FABRIC_TEXT_ELEMENT:
    case FABRIC_ITEXT_ELEMENT:
    case FABRIC_TEXTBOX_ELEMENT:
      return "text";
    case FABRIC_SVG_ELEMENT:
      return "shape";
    case FABRIC_IMAGE_ELEMENT:
      return "image";
    case FABRIC_GROUP_ELEMENT:
      return "group";
    case FABRIC_GIF_ELEMENT:
      return "giphy";
    case FABRIC_VIDEO_ELEMENT:
      return "video";
    case FABRIC_RECT_ELEMENT:
    case FABRIC_TRIANGLE_ELEMENT:
    case FABRIC_CIRCLE_ELEMENT:
    case FABRIC_ELLIPSE_ELEMENT:
    case FABRIC_POLYGON_ELEMENT:
      return "shape";
    default:
      return "text";
  }
};

export const getFabricTypeFromMime = (objType) => {
  switch (objType) {
    case "text":
      return "textbox";
    case "image":
      return "image";
    case "video":
      return "video";
    case "shape":
      return "svg";
    case "giphy":
      return "gif";
    default:
      return "text";
  }
};

export const getImageInfo = (source, data) => {
  let imageData = {
    sourceData: {
      source: null,
      source_url: null,
      source_id: null,
    },
  };
  switch (source) {
    case ImageSources.UNSPLASH:
      imageData["url"] = data?.urls?.regular || "";
      imageData["imageAspectRatio"] = data?.width / data?.height;
      imageData["thumbURL"] = data?.urls?.thumb;
      imageData["width"] = data?.width;
      imageData["height"] = data?.height;

      imageData["sourceData"]["source"] = SourceTypes.UNSPLASH;
      imageData["sourceData"]["source_url"] = data?.urls?.regular;
      imageData["sourceData"]["source_id"] = data?.id;

      break;
    case ImageSources.PEXELS:
      imageData["url"] =
        data?.src?.large2x || data?.src?.large || data?.src?.original;
      imageData["imageAspectRatio"] = data.width / data.height;
      imageData["thumbURL"] = data?.src?.medium;
      imageData["width"] = data?.width;
      imageData["height"] = data?.height;

      imageData["sourceData"]["source"] = SourceTypes.PEXELS;
      imageData["sourceData"]["source_url"] = imageData["url"]; // use same url as imageData.url
      imageData["sourceData"]["source_id"] = data?.id;
      break;
    case ImageSources.PIXABAY:
      imageData["url"] = data?.largeImageURL;
      imageData["imageAspectRatio"] = data?.imageWidth / data?.imageHeight;
      imageData["thumbURL"] = data?.previewURL;
      imageData["width"] = data?.imageWidth;
      imageData["height"] = data?.imageHeight;

      imageData["sourceData"]["source"] = SourceTypes.PIXABAY;
      imageData["sourceData"]["source_url"] = data?.largeImageURL;
      imageData["sourceData"]["source_id"] = data?.id;
      break;
    case ImageSources.SHOPIFY:
      imageData["url"] = data?.node?.featuredImage?.originalSrc;
      imageData["imageAspectRatio"] = 1.5;
      imageData["thumbURL"] = data?.node?.featuredImage?.originalSrc;
      imageData["width"] = 200;
      imageData["height"] = 200;

      imageData["sourceData"]["source"] = SourceTypes.SHOPIFY;
      imageData["sourceData"]["source_url"] =
        data?.node?.featuredImage?.originalSrc;
      imageData["sourceData"]["source_id"] = data?.node?.id;
      break;
    case ImageSources.GOOGLE_DRIVE:
      imageData["url"] = data?.thumbnailLink;
      imageData["thumbURL"] = data?.thumbnailLink;
      imageData["width"] =
        data?.imageMediaMetadata?.width || data?.imageMediaMetadata?.width > 0
          ? data?.imageMediaMetadata.width
          : 50;
      imageData["height"] =
        data?.imageMediaMetadata?.height || data?.imageMediaMetadata?.height > 0
          ? data?.imageMediaMetadata.height
          : 50;
      imageData["imageAspectRatio"] = imageData?.width / imageData?.height;

      imageData["sourceData"]["source"] = SourceTypes.GOOGLE_DRIVE;
      imageData["sourceData"]["source_url"] = data?.thumbnailLink;
      imageData["sourceData"]["source_id"] = data?.id;
      break;
    case ImageSources.SHUTTERSTOCK:
      imageData["url"] = data?.assets?.preview_1500.url;
      imageData["imageAspectRatio"] =
        data?.assets?.preview_1500.width / data?.assets?.preview_1500.height;
      imageData["app_id"] = ImageAppSources.CUSTOM;
      imageData["identifier"] = data?.assets.huge_thumb.url;

      imageData["imageAspectRatio"] =
        data?.assets?.preview_1500.width / data?.assets?.preview_1500.height;
      imageData["thumbURL"] = data?.huge_thumb?.url;
      imageData["width"] = data?.assets?.preview_1500.width;
      imageData["height"] = data?.assets?.preview_1500.height;

      imageData["sourceData"]["source"] = SourceTypes.SHUTTERSTOCK;
      imageData["sourceData"]["source_url"] = data?.assets?.preview_1500.url;
      imageData["sourceData"]["source_id"] = data?.id;
      break;
    case ImageSources.STORYBLOCKS:
      imageData["url"] = data?.preview_url;
      imageData["imageAspectRatio"] = data?.aspectRatio;
      imageData["thumbURL"] = data?.thumbnail_url;
      imageData["width"] = 200;
      imageData["height"] = 200 / data?.aspectRatio;

      imageData["sourceData"]["source"] = SourceTypes.STORYBLOCKS_GRAPHIC;
      imageData["sourceData"]["source_url"] = data?.preview_url;
      imageData["sourceData"]["source_id"] = data?.id;
      break;
    default:
      return;
  }
  return imageData;
};

/**
 *
 * @param imageSource
 * @param {errors} errors Errors Object
 * @returns {Object} {actionText: string, errorsText: Array<str>, actionURL: string}
 */
export const getImageProviderErrorsData = (imageSource, errors) => {
  const errorsData = {
    actionText: errors.action_text || null,
    errorsText:
      errors.non_field_errors ||
      `Something went wrong while fetching data from ${imageSource}`,
    actionURL: MY_APPS,
    iconComponent: null,
  };

  switch (
    imageSource

    // Modify `errorsData` according to the `imageSource`
  ) {
  }
  return errorsData;
};

export const getSourceCreditInfoData = (source, props, meta) => {
  let sourceCreditInfo = { sourceSiteName: source };

  if (source === ImageSources.PEXELS && props.data.mime === "video") {
    sourceCreditInfo["authorName"] = meta.user?.name;
    sourceCreditInfo["authorLink"] = meta.user?.url;
    sourceCreditInfo["sourceSiteLink"] = "https://pexels.com/";
  } else if (
    source === ImageSources.UNSPLASH ||
    source === ImageSources.PEXELS
  ) {
    sourceCreditInfo["authorName"] = meta?.username;
    sourceCreditInfo["authorLink"] =
      meta.profile + "?utm_source=Simplified&utm_medium=referral";
    sourceCreditInfo["sourceSiteLink"] =
      meta.source_location + "?utm_source=Simplified&utm_medium=referral";
  } else if (source === "Giphy") {
    sourceCreditInfo["authorName"] = meta.user ? meta.user?.display_name : "";
    sourceCreditInfo["authorLink"] = meta.user ? meta.user?.profile_url : "";
    sourceCreditInfo["sourceSiteLink"] = "https://giphy.com/";
  } else if (source === ImageSources.PIXABAY) {
    sourceCreditInfo["authorName"] = meta?.user;
    sourceCreditInfo["authorLink"] = meta?.pageURL;
    sourceCreditInfo["sourceSiteLink"] = "https://pixabay.com/";
  } else if (source === "Flaticon") {
    sourceCreditInfo["sourceSiteLink"] = "https://flaticon.com/";
  } else if (source === "Shopify") {
    sourceCreditInfo["authorName"] = meta?.user;
  } else if (source === "Shutterstock" && meta?.media_type === "video") {
    sourceCreditInfo["authorName"] = meta.contributor?.id;
    sourceCreditInfo["sourceSiteLink"] = "https://www.shutterstock.com/";
  } else if (source === "Shutterstock") {
    sourceCreditInfo["authorName"] = meta?.user;
    sourceCreditInfo["sourceSiteLink"] = "https://www.shutterstock.com/";
  }

  return sourceCreditInfo;
};

export const getVideoDetails = (source, video) => {
  let videoInfo = {
    sourceData: {
      source: null,
      source_id: null,
      source_url: null,
    },
  };

  if (source === VideoSources.PIXABAY) {
    videoInfo["height"] = video?.videos?.medium?.height;
    videoInfo["width"] = video?.videos?.medium?.width;
    videoInfo["videoCoverUrl"] = format(
      PIXABAY_VIDEO_COVER_ENDPOINT,
      video.picture_id,
      "200x150"
    );
    videoInfo["url"] = video?.videos?.medium?.url;
    videoInfo["duration"] = video?.duration;

    videoInfo["sourceData"]["source"] = SourceTypes.PIXABAY;
    videoInfo["sourceData"]["source_id"] = video?.picture_id;
    videoInfo["sourceData"]["source_url"] = video?.videos?.medium?.url;
  } else if (source === VideoSources.PEXELS) {
    videoInfo["height"] = video?.video_files[0].height;
    videoInfo["width"] = video?.video_files[0].width;
    videoInfo["videoCoverUrl"] = video?.image.replace(
      "w=1200&h=630",
      "w=200&h=150"
    );
    videoInfo["url"] = video?.video_files[0].link;
    videoInfo["duration"] = video?.duration;

    videoInfo["sourceData"]["source"] = SourceTypes.PEXELS;
    videoInfo["sourceData"]["source_id"] = video?.id;
    videoInfo["sourceData"]["source_url"] = video?.video_files[0].link;
  } else if (source === VideoSources.SHUTTERSTOCK) {
    videoInfo["height"] = 336;
    videoInfo["width"] = 596;
    videoInfo["videoCoverUrl"] = video?.assets?.thumb_jpg.url;
    videoInfo["url"] = video?.assets?.preview_mp4.url;
    videoInfo["duration"] = video?.duration;

    videoInfo["sourceData"]["source"] = SourceTypes.SHUTTERSTOCK;
    videoInfo["sourceData"]["source_id"] = video?.id;
    videoInfo["sourceData"]["source_url"] = video?.video_files[0].link;
  } else if (source === VideoSources.STORYBLOCKS) {
    videoInfo["height"] = 720;
    videoInfo["width"] = 1280;
    videoInfo["videoCoverUrl"] = video?.thumbnail_url;
    videoInfo["url"] = video?.preview_urls?._720p;
    videoInfo["duration"] = video?.duration;

    videoInfo["sourceData"]["source"] = SourceTypes.STORYBLOCKS_VIDEO;
    videoInfo["sourceData"]["source_id"] = video?.id;
    videoInfo["sourceData"]["source_url"] = video?.preview_urls?._720p;
  }

  return videoInfo;
};

export const prepareVideoPayload = (
  videoSource,
  video,
  contentSize,
  containerId
) => {
  let videoDetails = getVideoDetails(videoSource, video);

  if (!videoDetails) {
    return;
  }

  const height = videoDetails.height;
  const width = videoDetails.width;
  const videoCoverUrl = videoDetails.videoCoverUrl;

  const elementSize = { width, height };
  return {
    mime: "video",
    payload: {
      superType: "image",
      type: FABRIC_VIDEO_ELEMENT,
      width: elementSize.width,
      height: elementSize.height,
      name: "New video",
      autoplay: false,
      // muted: false,
      loop: false,
      src: videoDetails.url || "",
      size: elementSize,
      position: getAlignPosition(
        elementSize,
        { x: 0, y: 0 },
        "middle-center",
        contentSize
      ),
      container: containerId,
      cover: videoCoverUrl,
      startTime: 0,
      endTime: videoDetails.duration,
      duration: videoDetails.duration,
      userProperty: {
        source_id: videoDetails.sourceData.source_id,
      },
      volume: 1,
    },
    content: {
      source: videoDetails.sourceData.source,
      source_id: videoDetails.sourceData.source_id,
      source_url: videoDetails.sourceData.source_url,
      url: videoCoverUrl,
      // TODO: omit `duration` after full deprecation
      meta: { video, duration: videoDetails.duration },
    },
  };
};

export const onClickSetAsBackground = (
  canvasRef,
  imageUrl,
  contentData = {}
) => {
  let activeObj;
  let imageURL;
  let filters = [];
  let userProperty = {};
  if (imageUrl) {
    imageURL = imageUrl;
    filters = canvasRef.handler.workarea.filters;
    userProperty = { source_id: contentData?.source_id };
  } else {
    activeObj = canvasRef.handler.canvas.getActiveObject();
    if (!activeObj || activeObj.type !== "image" || activeObj.src === null) {
      return;
    }
    filters = activeObj.filters;
    imageURL = activeObj.src;
  }

  let objectPayload;

  if (activeObj) {
    objectPayload = activeObj;
    canvasRef.handler.remove(activeObj, true);
  } else {
    objectPayload = {
      src: imageURL,
      filters: filters,
      userProperty,
    };
  }

  canvasRef.handler.workareaHandler
    .setImagePattern(objectPayload)
    .then((workarea) => {
      canvasRef.handler.canvas.fire("object:modified", {
        target: workarea,
        message: { content: contentData },
      });
    });

  // to close the context menu
  if (canvasRef.handler.contextmenuHandler) {
    canvasRef.handler.contextmenuHandler.hide();
  }
};

export const onSetAsBackgroundRenderArtboard = async (canvasRef) => {
  let activeObj = canvasRef.handler.canvas.getActiveObject();
  if (!activeObj || activeObj.type !== "image" || activeObj.src === null) {
    return;
  }
  let filters = activeObj.filters;
  let imageURL = activeObj.src;

  let objectPayload;

  if (activeObj) {
    objectPayload = {
      ...activeObj,
      userProperty: activeObj.userProperty ? { ...activeObj.userProperty } : {},
    };
    canvasRef.handler.remove(activeObj, false);

    const staticHandler = canvasRef.handler.staticHandler;

    if (staticHandler) {
      const { canvas } = staticHandler;

      const staticObjects = canvas.getObjects();
      const removedObj =  staticObjects.find(item => item.id === activeObj.id);

      canvas.remove(removedObj);
      canvas.renderAll();
    }

  } else {
    objectPayload = {
      src: imageURL,
      filters: filters,
    };
  }
  await canvasRef.handler.workareaHandler.setImagePattern(objectPayload);
  // to close the context menu
  if (canvasRef.handler.contextmenuHandler) {
    canvasRef.handler.contextmenuHandler.hide();
  }
};

export const alignObject = (action, canvasRef) => {
  switch (action) {
    case "top":
      canvasRef.handler.objectHandler.alignTop();
      break;
    case "middle":
      canvasRef.handler.objectHandler.alignMiddle();
      break;
    case "bottom":
      canvasRef.handler.objectHandler.alignBottom();
      break;
    case "left":
      canvasRef.handler.objectHandler.alignLeft();
      break;
    case "center":
      canvasRef.handler.objectHandler.alignCenter();
      break;
    case "right":
      canvasRef.handler.objectHandler.alignRight();
      break;
    default:
      return;
  }

  // to close the context menu
  if (canvasRef.handler.contextmenuHandler) {
    canvasRef.handler.contextmenuHandler.hide();
  }
};

export const convertColor = (color, convertType) => {
  if (!color) return;

  const fabricColor = new fabric.Color(color);
  const source = fabricColor.getSource();

  switch (convertType) {
    case "hex":
      return `#${fabricColor.toHex()}`;
    case "hsl":
      return fabricColor.toHsl();
    case "hsla":
      return fabricColor.toHsla();
    case "rgb":
      return fabricColor.toRgb();
    case "rgba":
      return fabricColor.toRgba();
    case "rgb-object":
      return { r: source[0], g: source[1], b: source[2], a: source[3] };
    default:
      return color;
  }
};

export const triggerDownload = (url) => {
  if (url) {
    const anchorEl = document.createElement("a");
    anchorEl.href = url;
    anchorEl.download = "";
    document.body.appendChild(anchorEl); // required for firefox
    anchorEl.click();
    anchorEl.remove();
  }
};
export const getFontVariants = (fonts, font) => {
  let fontInfo = find(fonts, ["family", font]);
  if (fontInfo && fontInfo.payload && fontInfo.payload.variants) {
    return fontInfo.payload.variants;
  }
  return [];
};

export const getVariantName = (fontInfo, varient) => {
  let url = fontInfo?.payload?.files[varient];
  if (url) {
    let fontName = url.substring(url.lastIndexOf("/") + 1).split(".")[0];
    let fontWeight = fontName?.split("-")[1];
    if (fontWeight) {
      return fontWeight;
    }
  }
  return varient;
};

export const getFont = (fonts, font) => {
  return find(fonts, ["family", font]);
};

export const calculateIconColors = (element) => {
  const { format } = element;
  let colors = [];

  if (!format || format.type !== FABRIC_SVG_ELEMENT) return 0;

  if (format.objects) {
    format.objects.forEach((color) => {
      if (typeof color.fill !== "object") colors.push(color.fill);
      else colors.push("rgba(0, 0, 0, 0)");
    });
  } else if (format._objects) {
    format._objects.forEach((color) => {
      if (typeof color.fill !== "object") colors.push(color.fill);
    });
  }
  colors = [...new Set(colors)];
  return colors.length;
};

export const checkInputboxValueRangeForSlider = (number, range) => {
  if (!number || !range) return;

  return inRange(number, range[0], range[1] + 1);
};

export const formatDuration = (seconds) => {
  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = pad(date.getUTCSeconds());
  if (hh) {
    return `${hh}:${pad(mm)}:${ss}`;
  }
  return `${mm}:${ss}`;
};

export const formatSelectedDuration = (seconds) => {
  seconds = Number(seconds.toFixed(1));

  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = date.getUTCSeconds();
  if (hh) {
    return `${hh}:${pad(mm)}:${ss} hrs`;
  }
  if (mm) {
    return `${mm}:${pad(ss)} mins`;
  }
  return `${ss} secs`;
};

const pad = (string) => {
  return ("0" + string).slice(-2);
};

export const getPayloadFromShopify = (object) => {
  return {
    page: "",
    layers: [
      {
        name: "productName",
        properties: {
          text: object.node.title,
        },
      },
      {
        name: "productPrice",
        properties: {
          text:
            object.node.priceRangeV2.maxVariantPrice.currencyCode +
            object.node.priceRangeV2.maxVariantPrice.amount,
        },
      },
      {
        name: "featuredImage",
        properties: {
          src: object.node.featuredImage.originalSrc,
        },
      },
    ],
  };
};
export const angleToRect = (angle, sx, sy) => {
  while (angle < 0) {
    angle += 360;
  }
  angle %= 360;

  var a = sy,
    b = a + sx,
    c = b + sy,
    p = (sx + sy) * 2,
    rp = p * 0.00277,
    pp = Math.round((angle * rp + (sy >> 1)) % p);

  if (pp <= a) {
    return {
      x: 0,
      y: sy - pp,
    };
  } else if (pp <= b) {
    return {
      y: 0,
      x: pp - a,
    };
  } else if (pp <= c) {
    return {
      x: sx,
      y: pp - b,
    };
  } else {
    return {
      y: sy,
      x: sx - (pp - c),
    };
  }
};

export const adjustColorOffsets = (colorsData) => {
  var newData = [];
  colorsData.forEach((color) => {
    newData.push({
      ...color,
      offset: color.offset > 1 ? color.offset / 100 : color.offset,
    });
  });

  return newData;
};

export const getArtBoardSizeToFitAvailableArea = (
  artboardSize,
  availableAreaSize
) => {
  const { width: previewAreaWidth, height: previewAreaHeight } =
    availableAreaSize;

  const { width: maxWidth, height: maxHeight } = artboardSize;

  const artBoardAspectRatio = maxWidth / maxHeight;
  const availableAreaAspectRatio = previewAreaWidth / previewAreaHeight;

  let artBoardHeight,
    artBoardWidth,
    artBoardWidthPercentage,
    artBoardHeightPercentage;

  if (artBoardAspectRatio > availableAreaAspectRatio) {
    artBoardWidthPercentage = 90;

    // px
    artBoardWidth = previewAreaWidth * (artBoardWidthPercentage / 100);
    artBoardHeight = artBoardWidth / artBoardAspectRatio;

    // %
    artBoardHeightPercentage = (100 * artBoardHeight) / previewAreaHeight;
  } else {
    artBoardHeightPercentage = 90; // % of canvas area occupied by artboard height

    // px
    artBoardHeight = previewAreaHeight * (artBoardHeightPercentage / 100);
    artBoardWidth = artBoardAspectRatio * artBoardHeight;

    // %
    artBoardWidthPercentage = (100 * artBoardWidth) / previewAreaWidth;
  }

  return {
    heightPercentage: artBoardHeightPercentage,
    widthPercentage: artBoardWidthPercentage,
    height: artBoardHeight,
    width: artBoardWidth,
  };
};

export const isURL = (url) => {
  try {
    return Boolean(new URL(url));
  } catch (e) {
    return false;
  }
};

/**
 *
 * @param {String} taskId celery task id
 * @param {Function} onAddTimer(timerId) | Called with timer id
 * @param interval
 * @returns {Promise}
 */
export const checkCeleryTaskStatus = (
  taskId,
  onAddTimer = () => {},
  interval = 5000
) => {
  return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      axios
        .get(Format(CHECK_TASK_STATAUS, taskId))
        .then((res) => {
          if (res.data?.status === "SUCCESS") {
            clearInterval(timer);
            resolve({ taskId: taskId, response: res.data });
          } else if (res.data?.status === "FAILURE") {
            clearInterval(timer);
            reject({
              taskId: taskId,
              response: res.data,
              status: res.data?.status,
            });
          }
        })
        .catch((error) => {
          clearInterval(timer);
          reject(error);
        });
    }, interval);
    onAddTimer(timer);
  });
};
export const checkCeleryTaskStatusBg = (
  taskId,
  onAddTimer = () => {},
  interval = 5000
) => {
  return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      axios
        .get(Format(CHECK_TASK_STATUS, taskId), {
          headers: { Authorization: "" },
        })
        .then((res) => {
          if (res.data?.status === "SUCCESS") {
            clearInterval(timer);
            resolve({ taskId: taskId, response: res.data });
          } else if (res.data?.status === "FAILURE") {
            clearInterval(timer);
            reject({
              taskId: taskId,
              response: res.data,
              status: res.data?.status,
            });
          }
        })
        .catch((error) => {
          clearInterval(timer);
          reject(error);
        });
    }, interval);
    onAddTimer(timer);
  });
};

/**
 *
 * @param {Object} item
 * @param cancelToken
 * @param {function} onAddTimer(timer)
 * @param props
 * @returns {Promise} Promise returns updated item data
 */
export const genericImageDownload = async (
  item,
  cancelToken,
  onAddTimer,
  props = {}
) => {
  const { editor: { activePage: { id: activePageId } = {} } = {} } = props;

  const payload = {
    app_id: item?.content?.source,
    identifier: item?.content?.source_id,
    extra_data: { ...item, page: activePageId },
    create_asset: false,
  };
  showToast({
    description: "Please wait while image is being processed",
    title: "Processing image",
    status: "success",
  });

  return axios
    .post(GENERIC_IMAGE_DOWNLOAD, payload, {
      cancelToken,
    })
    .then((res) => {
      return checkCeleryTaskStatus(res.data?.task_id, onAddTimer);
    })
    .then((taskResponse) => {
      const contentUrl = taskResponse?.response?.info?.url;
      item.content.url = contentUrl;
      item.payload.src = contentUrl;
      item.payload.width = taskResponse?.response?.info?.width;
      item.payload.height = taskResponse?.response?.info?.height;

      showToast({
        description: "Adding image to artboard",
        title: "Adding image",
        status: "success",
      });

      return Promise.resolve(item);
    });
};

export const analytics = Analytics({
  app: "Simplified",
  plugins: [
    googleTagManager({
      containerId: process.env.REACT_APP_GOOGLETAGMANAGER_CONTAINERID,
    }),
  ],
});

export const analyticsTrackEvent = (eventName, eventData) => {
  if (
    process.env.REACT_APP_ENV === "production" ||
    process.env.REACT_APP_ENV === "prod"
  ) {
    const userData = store.getState().auth?.payload?.user;

    if (userData) {
      analytics.track("tldr." + eventName, {
        eventData: eventData,
        userId: userData?.pk,
        email: userData?.email,
        username: userData?.username,
      });
    } else {
      analytics.track("tldr." + eventName, eventData);
    }
  }
};

export const saveDataURLToFile = (dataURL, option) => {
  if (dataURL) {
    const anchorEl = document.createElement("a");
    anchorEl.href = dataURL;
    anchorEl.download = `${option.name}.${option.format}`;
    document.body.appendChild(anchorEl); // required for firefox
    anchorEl.click();
    anchorEl.remove();
  } else {
    console.error(
      "There is an error while exporting artboard as image. Contact to support center."
    );
  }
};

export const endsWithAny = (suffixes, string) => {
  for (const suffix of suffixes) {
    if (string.endsWith(suffix)) {
      return true;
    }
  }
  return false;
};

/**
 * Makes API call to connect to partner application
 * @param {Object} payload {}
 * @param appId
 * @param signalToken
 */

export const genericAppConnect = (
  appId,
  {
    accessToken,
    refreshToken,
    code,
    data,
    callbackUrl = DOMAIN_NAME,
    clientType,
    queryParams = {},
  },
  signalToken
) => {
  let url = GENERIC_APP_CONNECT;
  if (queryParams) {
    const query = queryString.stringify(queryParams);
    url += `?${query}`;
  }

  return new Promise((resolve, reject) => {
    axios
      .post(
        url,
        {
          app_id: appId,
          access_token: accessToken,
          refresh_token: refreshToken,
          code: code,
          data: data,
          callback_url: callbackUrl,
          client_type: clientType,
        },
        {
          cancelToken: signalToken,
        }
      )
      .then((res) => {
        resolve(res.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const formatSize = (props, storySize) => {
  let format = ``;
  const activePageId = props?.editor?.activePage?.id;
  const activePagePayload = props?.pagestore?.pages[activePageId]?.payload;
  if (activePageId && !isEmpty(activePagePayload)) {
    format = `${activePagePayload.width}x${activePagePayload.height}`;
  } else if (!isEmpty(storySize)) {
    format = `${storySize.width}x${storySize.height}`;
  }
  return format;
};

export const getFeatureLimit = (subscription, featureCode) => {
  const { availableFeatures } = subscription;

  return availableFeatures.find(
    (feature) => feature.feature_code === featureCode
  )?.usage_limit;
};

export const isFeatureAvailable = (
  subscription,
  featureCode,
  availableOnTrial = true
) => {
  const { subscribedPlan, availableFeatures } = subscription;

  const isTrialActive =
    subscribedPlan?.trial_remaining_days !== null &&
    subscribedPlan?.trial_remaining_days >= 0;

  if (isTrialActive && availableOnTrial) {
    return true;
  }

  if (isTrialActive) {
    return false;
  }

  const featureCodeIndex = availableFeatures.findIndex(
    (feature) => feature.feature_code === featureCode
  );

  return featureCodeIndex > -1;
};

export const updateLayerAnimation = (
  totalDuration,
  currentLayerAnimations,
  adjustment,
  payload,
  animationType = ENTER_ANIMATION
) => {
  let startTime = 0;
  let animationDuration = 0;
  let stillAnimationDuration = 0;
  let enterAnimationDuration = 0;
  let exitAnimationDuration = 0;

  if (currentLayerAnimations && currentLayerAnimations.length > 0) {
    startTime = currentLayerAnimations[0].start_time;
    currentLayerAnimations.forEach((animation) => {
      animationDuration += animation.trans_duration ?? 0;
      if (animation.motion === ENTER_ANIMATION) {
        enterAnimationDuration += animation.trans_duration;
      } else if (animation.motion === EXIT_ANIMATION) {
        exitAnimationDuration += animation.trans_duration;
      } else {
        stillAnimationDuration += animation.trans_duration;
      }
    });
  } else {
    stillAnimationDuration = totalDuration;
    animationDuration = totalDuration;
  }

  // if (currentLayerAnimations && currentLayerAnimations.length === 2) {
  //   startTime = currentLayerAnimations[0].start_time;
  //   endTime = currentLayerAnimations[1].end_time;
  //   totalDuration = endTime - startTime;
  // } else if (currentLayerAnimations && currentLayerAnimations.length === 1) {
  //   startTime = currentLayerAnimations[0].start_time;
  //   endTime = currentLayerAnimations[0].end_time;
  //   totalDuration = endTime - startTime;
  // } else if (!currentLayerAnimations && payload.type === FABRIC_VIDEO_ELEMENT) {
  //   endTime = payload.endTime * 1000;
  //   totalDuration = endTime - startTime;
  // }

  // If no animations then return the still animation only
  if (!currentLayerAnimations) {
    currentLayerAnimations = [
      {
        ...STILL_EFFECT,
        start_time: startTime,
        trans_duration: animationDuration,
        end_time: animationDuration,
      },
    ];
  }

  let updatedAnimations = [];

  if (adjustment.id === "none") {
    // If None type animation is selected for enter or exit animation
    let stillAnimationAdjustment = 0;
    if (animationType === ENTER_ANIMATION) {
      stillAnimationAdjustment = enterAnimationDuration;
    } else if (animationType === EXIT_ANIMATION) {
      stillAnimationAdjustment = exitAnimationDuration;
    }

    // Remove the selected animation type
    updatedAnimations = currentLayerAnimations.filter(
      (o) => o.motion !== animationType
    );

    // Rebuild animations
    let prevOffset = startTime;
    updatedAnimations = updatedAnimations.map((animation) => {
      animation.start_time = prevOffset;

      // Update the still animations
      if (animation.motion === "still") {
        animation.trans_duration += stillAnimationAdjustment;
      }
      animation.end_time = animation.start_time + animation.trans_duration;
      prevOffset = animation.end_time;
      return {
        ...animation,
      };
    });
  } else {
    // Is selected/changed animation type is present in the current animations
    const isSelectedAnimationTypeExists = !!currentLayerAnimations.find(
      (o) => o.motion === animationType
    );

    if (isSelectedAnimationTypeExists) {
      updatedAnimations = currentLayerAnimations.map((animation) => {
        // Replace the animation info except start_time, end_time and trans_duration
        if (animation.motion === animationType) {
          const selectedAnimation = adjustment.variants[0];
          return {
            ...selectedAnimation,
            start_time: animation.start_time,
            trans_duration: animation.trans_duration,
            end_time: animation.end_time,
          };
        }
        return {
          ...animation,
        };
      });
    } else {
      let selectedAnimation = adjustment.variants[0];
      let stillAnimationAdjustment = selectedAnimation.trans_duration ?? 0;

      // If still animation is too small then
      if (stillAnimationDuration - stillAnimationAdjustment < 500) {
        selectedAnimation = {
          ...selectedAnimation,
          trans_duration: stillAnimationDuration / 2,
        };
        stillAnimationAdjustment = stillAnimationDuration / 2;
      }
      if (animationType === ENTER_ANIMATION) {
        // If Changed animation type is enter animation
        updatedAnimations = [
          {
            ...selectedAnimation,
          },
          ...currentLayerAnimations,
        ];
      } else if (animationType === EXIT_ANIMATION) {
        // If Changed animation type is exit animation
        updatedAnimations = [
          ...currentLayerAnimations,
          {
            ...selectedAnimation,
          },
        ];
      }

      let prevOffset = startTime;
      updatedAnimations = updatedAnimations.map((animation) => {
        const startTime = prevOffset;
        if (animation.motion === "still") {
          animation.trans_duration -= stillAnimationAdjustment;
        }
        const endTime = startTime + animation.trans_duration;
        prevOffset = endTime;
        return {
          ...animation,
          start_time: startTime,
          end_time: endTime,
        };
      });
    }
  }

  // if (adjustment.id === "none") {
  //   const still = { ...STILL_EFFECT };
  //   still.start_time = startTime;
  //   still.end_time = endTime;
  //   still.trans_duration = totalDuration;
  //   updatedAnimations = [still];
  // } else {
  //   const enterAnimation = adjustment.variants[0].enter;
  //   const still = STILL_ANIMATION.variants[0].enter;
  //
  //   switch (currentLayerAnimations ? currentLayerAnimations.length : 2) {
  //     case 3:
  //     case 2:
  //     case 1:
  //       updatedAnimations = updateAnimationTimings(
  //         enterAnimation,
  //         still,
  //         totalDuration,
  //         startTime
  //       );
  //       break;
  //     default:
  //   }
  // }

  return updatedAnimations;
};

export const encodeWithDash = (input) => {
  if (!input) {
    return "";
  }
  return input.replace(/\s+/g, "-").toLowerCase();
};

export const getMusicPayload = (source, payload) => {
  let musicPayload = { ...defaultMusicPayload };

  switch (source) {
    case MusicSources.STORYBLOCKS:
      musicPayload.title = payload?.title;
      musicPayload.src = payload?.preview_url;
      musicPayload.duration = payload?.duration;
      musicPayload.bpm = payload?.bpm;
      musicPayload.source = SourceTypes.STORYBLOCKS_AUDIO;
      musicPayload.source_id = payload?.id;
      musicPayload.source_url = payload?.preview_url;
      musicPayload.cover = payload?.thumbnail_url;
      musicPayload.meta = {
        thumbnail: payload?.thumbnail_url,
        preview_url: payload?.preview_url,
      };
      break;
    case MusicSources.ASSETS:
      musicPayload.title = payload?.title ?? "New Music";
      musicPayload.src = payload?.payload?.src;
      musicPayload.source_url = payload?.content?.source_url;
      musicPayload.source_id = payload?.content?.source_id;
      musicPayload.source = SourceTypes.TLDR_ASSETS;
      musicPayload.duration = payload?.content?.meta?.payload?.duration;
      musicPayload.content = payload?.content;
      musicPayload.bpm = null;
      break;

    default:
      musicPayload.title = "New Music";
      musicPayload.src = payload?.payload?.src;
      musicPayload.duration = payload?.content?.meta?.payload?.duration;
      musicPayload.bpm = null;

      break;
  }
  return musicPayload;
};

export const audioMimeTypes = ["audio/mpeg", "audio/vnd.wav"];

export const getMediaDurationFromFile = (source) => {
  return new Promise((resolve, reject) => {
    let src = null;
    if (isURL(source)) {
      src = source;
    } else if (
      !audioMimeTypes.includes(source.type) &&
      !source.type.includes("video")
    ) {
      resolve(0);
    } else {
      src = URL.createObjectURL(source);
    }
    if (src) {
      if (audioMimeTypes.includes(source.type)) {
        let audio = new Audio();
        audio.onloadedmetadata = () => {
          resolve(audio.duration);
        };
        audio.onerror = reject;
        audio.src = src;
      } else if (source.type.includes("video")) {
        let video = document.createElement("video");
        video.onloadedmetadata = () => {
          resolve(video.duration);
        };
        video.onerror = reject;
        video.src = src;
        video.preload = "metadata";
      } else {
        resolve(0);
      }
    }
  });
};

export const showCreatePresetFloatingButton = (path) => {
  return (
    path === ROOT ||
    path === QUICK_START ||
    path === PROJECTS ||
    path === TEMPLATES_SCREEN
  );
};

export const showBottomMenuBar = (path) => {
  return (
    path === ROOT ||
    path === QUICK_START ||
    path === PROJECTS ||
    path === TEMPLATES_SCREEN ||
    path === LAYOUTS ||
    path === MAGICAL ||
    path === DOCUMENTS ||
    path === ANALYTICS ||
    path === FAVORITES ||
    path === PUBLISHING
  );
};

export const isMobileView = () => {
  return window.innerWidth < 768;
};

export const isWhiteColor = (color) => {
  if(isEmpty(color)){
    return false;
  }
  color = color.toLowerCase();
  return (
    color === "rgba(255,255,255,1)" ||
    color === "white" ||
    color === "#ffffff" ||
    color === "#fff"
  );
};

export const isAuthorizeToChangeWorkspaceUserRole = (
  authenticatedUserId,
  workspaceMembers
) => {
  let isAuthorizeToChangeRole = false;
  workspaceMembers.forEach((member) => {
    if (authenticatedUserId === member?.user_id) {
      isAuthorizeToChangeRole =
        member?.is_admin ||
        member?.is_primary ||
        member?.role?.name === ROLE_WORKSPACE_OWNER_CODE ||
        member?.role?.name === ROLE_WORKSPACE_ADMIN_CODE;
    }
  });

  return isAuthorizeToChangeRole;
};

export const isWorkspaceOwner = (member) => {
  return (
    member.is_primary ||
    member.role === ROLE_WORKSPACE_OWNER_CODE ||
    member.role?.name === ROLE_WORKSPACE_OWNER_CODE
  );
};

export function roundToNearest(numToRound, numToRoundTo) {
  return Math.round(numToRound / numToRoundTo) * numToRoundTo;
}

export const URLSearchParamsCustom = (w) => {
  w.URLSearchParams =
    w.URLSearchParams ||
    function (searchString) {
      var self = this;
      self.searchString = searchString;
      self.get = function (name) {
        var results = new RegExp("[?&]" + name + "=([^&#]*)").exec(
          self.searchString
        );
        if (results == null) {
          return null;
        } else {
          return decodeURIComponent(results[1]) || 0;
        }
      };
    };
};

export const isColor = (strColor) => {
  const s = new Option().style;
  s.color = strColor;
  return s.color !== "";
};

export const toPascalCase = (str) => {
  return startCase(camelCase(str)).replace(/ /g, "");
};

export const checkArtboardLimit = (pagestore, subscription) => {
  if (isFeatureAvailable(subscription, FeatureCodes.VIDEO_ARTBOARD_COUNT)) {
    const { pageIds = [] } = pagestore;
    const artboardLimit = getFeatureLimit(
      subscription,
      FeatureCodes.VIDEO_ARTBOARD_COUNT
    );
    if (pageIds.length < artboardLimit) {
      return true;
    } else {
      showToast({
        description: `Can't add more than ${artboardLimit} Artboards.`,
        title: "Error",
        status: "error",
      });

      return false;
    }
  }
  return true;
};

/**
 * Get story stats for passed pages payload
 * - Story's pages height and width are consistent or not
 * - Total duration of the story pages
 * @param pages
 * @returns {[boolean, number]} [isStorySizeConsistent, storyDuration]
 */

export const getStoryStats = (pages) => {
  let isStorySizeConsistent = true;
  let storyDuration = 0;

  if (!pages || pages.length === 0) return [true, 0];

  const basePage = pages[0];

  for (const page of pages) {
    if (page?.payload?.height !== basePage?.payload?.height) {
      isStorySizeConsistent = isStorySizeConsistent && false;
    }
    if (page?.payload?.width !== basePage?.payload?.width) {
      isStorySizeConsistent = isStorySizeConsistent && false;
    }
    storyDuration += page?.payload?.animation?.duration ?? 0;
  }

  return [isStorySizeConsistent, storyDuration];
};

export const roundNumber = (value, convertToString) => {
    const num = Number(value)
    const billions = num/1.0e+9
    const millions = num/1.0e+6
    const thousands = num/1.0e+3
    const nrRounded = num >= 1.0e+9 && billions >= 100  ? Math.round(billions)  + "B"
         : num >= 1.0e+9 && billions >= 10   ? billions.toFixed(1)   + "B"
         : num >= 1.0e+9                     ? billions.toFixed(1)   + "B"
         : num >= 1.0e+6 && millions >= 100  ? Math.round(millions)  + "M"
         : num >= 1.0e+6 && millions >= 10   ? millions.toFixed(1)   + "M"
         : num >= 1.0e+6                     ? millions.toFixed(1)   + "M"
         : num >= 1.0e+3 && thousands >= 100 ? Math.round(thousands) + "k"
         : num >= 1.0e+3 && thousands >= 10  ? thousands.toFixed(1)  + "k"
         : num >= 1.0e+3                     ? thousands.toFixed(1)  + "k"
         : num.toFixed();

    if (convertToString) {
      return String(nrRounded).replace(".", ",");
    }

    return nrRounded;
}

export const calculatePercentageIncrease = (newValue, prevValue) => {
  let prev = prevValue === 0 ? 1 : prevValue; // default prev value to 1 to avoid showing Infinity in increase if prev value is 0;
  
  return newValue ? (newValue - prevValue) / prev * 100 : 0;
}

export const sumRangeData = (data) => {
  const sum = data.reduce((prev, current) => {
    return {
      value: prev.value + current.y,
      valuePrev: prev.valuePrev + current.yPrev,
    }
  }, { value: 0, valuePrev: 0 });

  return {
    value: roundNumber(sum.value, true),
    increase: calculatePercentageIncrease(sum.value, sum.valuePrev)
  };
};

